import type { ValueHumanDescription } from '../../components/Colorizer/types';
import {_} from 'svelte-i18n';
import { get } from 'svelte/store';

export class smartRounding {
    public getFormatedValue(
        value: number,
        unit: string = '',
        options: { convert_nicer: boolean }
    ): string {
        let { rounded_value, rounded_title } = this.getRoundedValues(value, unit, options);
        return (rounded_value.toLocaleString() + ' ' + rounded_title + ' ' + unit).replaceAll('  ', ' ').trim();
    }

    private static unitConversion = [
        {
            origin_unit: 'm',
            target_unit: 'km',
            divider: 1000,
        },
        {
            origin_unit: 'm2',
            target_unit: 'km2',
            divider: 1000000,
        },
    ];

    public getRoundedValues(
        value: number,
        origin_unit: string = '',
        options: { convert_nicer: boolean }
    ): ValueHumanDescription {
        if (isNaN(value)) {
            return {
                rounded_unit: origin_unit,
                non_rounded_value: 0,
                rounded_value: 0,
                rounded_title: '',
                rounded_divider: 1,
                original_value: value,
                original_unit: origin_unit,
            };
        }
        if (options?.convert_nicer && origin_unit === 'm') {
            let { rounded_unit, non_rounded_value, rounded_value, rounded_title, rounded_divider } =
                this.getRoundedValues(value / 1000, 'km', { convert_nicer: false });
            return {
                rounded_unit,
                non_rounded_value: Math.round((value / 1000) * 10) / 10,
                rounded_value,
                rounded_title,
                rounded_divider,
                original_value: value,
                original_unit: origin_unit,
            };
        }
        if (options?.convert_nicer && origin_unit === 'm2') {
            let { rounded_unit, non_rounded_value, rounded_value, rounded_title, rounded_divider } =
                this.getRoundedValues(value / 1000000, 'km2', { convert_nicer: false });
            return {
                rounded_unit,
                non_rounded_value: Math.round((value / 1000000) * 10) / 10,
                rounded_value,
                rounded_title,
                rounded_divider,
                original_value: value,
                original_unit: origin_unit,
            };
        }
        let rounded_value: number = value;
        let rounded_title: string = '';
        let rounded_divider: number = 1;
        // let rounded_unit = unit;
        try {
            if (Math.abs(value) >= 0) {
                rounded_divider = 1;
                rounded_value = Math.round(value * 10) / 10;
                // rounded_title = unit ?? '';
                rounded_title = '';
            }
            if (!['m', 'km'].includes(origin_unit)) {
                if (Math.abs(value) >= 1e3) {
                    rounded_divider = 1e3;
                    rounded_value = Math.round(value / 1e2) / 10;
                    // rounded_title = 'tys. ' + unit;
                    // rounded_title = 'tys.';
                    rounded_title = get(_)('round_multiplier_1e3');
                }
                if (Math.abs(value) >= 1e6) {
                    rounded_divider = 1e6;
                    rounded_value = Math.round(value / 1e5) / 10;
                    // rounded_title = 'mln ' + unit;
                    rounded_title = get(_)('round_multiplier_1e6');
                }
                if (Math.abs(value) >= 1e9) {
                    rounded_divider = 1e9;
                    rounded_value = Math.round(value / 1e8) / 10;
                    rounded_title = get(_)('round_multiplier_1e9');
                    // rounded_title = 'mld ' + unit;
                }
                if (Math.abs(value) >= 1e12) {
                    rounded_divider = 1e12;
                    rounded_value = Math.round(value / 1e11) / 10;
                    rounded_title = get(_)('round_multiplier_1e12');
                    // rounded_title = 'bln ' + unit;
                }
            }
        } catch (e) {
            console.log('error in getRoundedValues');
        }
        // console.log('value => text_value', value, rounded_value, rounded_title);
        return {
            rounded_unit: origin_unit,
            non_rounded_value: Math.round(value * 10) / 10,
            rounded_value:
                Math.abs(rounded_value) >= 10 ? Math.round(rounded_value) : rounded_value,
            rounded_title: rounded_title.trim(),
            rounded_divider,
            original_value: value,
            original_unit: origin_unit,
        };
    }

    public getRoundedValuesWithTheSameUnit(
        values: number[],
        unit: string = '',
        options: { convert_nicer: boolean }
    ): ValueHumanDescription[] {
        let bestRounded: ValueHumanDescription = {
            rounded_unit: unit,
            non_rounded_value: 0,
            rounded_value: 0,
            rounded_title: '',
            rounded_divider: 1,
            original_value: 0,
            original_unit: unit,
        };

        if (options?.convert_nicer && unit === 'm') {
            unit = 'km';
            values = values.map((value, index) => {
                return value / 1000;
            });
        }

        if (options?.convert_nicer && unit === 'm2') {
            unit = 'km2';
            values = values.map((value, index) => {
                return value / 1000000;
            });
        }

        values.forEach((value, index) => {
            let current = this.getRoundedValues(value, unit, options);
            if (current.rounded_divider > bestRounded.rounded_divider) {
                bestRounded = current;
            }
        });

        return values.map((value, index) => {
            return {
                rounded_unit: unit,
                non_rounded_value: value,
                rounded_value: this.getRoundedValueByDivider(value, bestRounded.rounded_divider),
                rounded_title: bestRounded.rounded_title,
                rounded_divider: bestRounded.rounded_divider,
                original_value: value,
                original_unit: unit,
            };
        });
    }

    public getRoundedValueByDivider(value: number, divider: number): number {
        let v = value;
        let rounder = 10;
        // round v until it has last digit not 0
        while (Math.round(v * rounder) / rounder === 0 && rounder < 1e10) {
            rounder *= 10;
        }
        v = Math.round((value / divider) * rounder) / rounder;
        if (Math.abs(v) >= 10) {
            return Math.round(v);
        }
        return v;
    }

    public getOriginalValueFromRounded(
        rounded_value: number,
        rounded_divider: number,
        current_unit: string,
        target_unit: string
    ): number {
        if (target_unit === 'm' && current_unit === 'km') {
            return rounded_value * rounded_divider * 1000;
        }
        if (target_unit === 'm2' && current_unit === 'km2') {
            return rounded_value * rounded_divider * 1000000;
        }
        return rounded_value * rounded_divider;
    }
}
